import { Fragment, useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { ApiError } from '@mds/shared/lib/api';
import type {
  AdminNotificationDelivery,
  NotificationDeliveryStatus,
} from '@mds/shared/types/notification-history';

import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import { controlCls } from '@/components/ui/field';
import Modal from '@/components/ui/modal';
import Select from '@mds/shared/ui/select';
import StatCard from '@/components/ui/stat-card';
import { Table, Row, Cell } from '@/components/ui/table';
import { Toolbar } from '@/components/ui/toolbar';
import { stamp } from '@mds/shared/lib/dates';
import { channelLabel, describeDetail, notificationLabel } from '@/lib/labels';
import * as notifications from '@/notifications/notifications.api';

const ALL = 'All';
const PAGE_SIZE = 25;

const STATUSES: NotificationDeliveryStatus[] = [
  'pending',
  'processing',
  'retry_scheduled',
  'delivered',
  'failed',
];

/** How each delivery state is drawn. Anything unresolved reads as a warning. */
const statusTone = (status: NotificationDeliveryStatus) =>
  status === 'delivered'
    ? 'border-brand-edge/35 bg-brand-wash text-brand-strong'
    : status === 'failed'
      ? 'border-danger-edge bg-danger-wash text-danger-text'
      : 'border-warn-edge bg-warn-wash text-warn-text';

/** "retry_scheduled" is the wire value; this is what an operator reads. */
const statusLabel = (status: NotificationDeliveryStatus) =>
  status === 'retry_scheduled' ? 'retrying' : status;

/** Delivered and failed rows never change again; everything else is still moving. */
const isTerminal = (status: NotificationDeliveryStatus) =>
  status === 'delivered' || status === 'failed';

const dayStart = (v: string) => (v ? new Date(`${v}T00:00:00`).toISOString() : undefined);
const dayEnd = (v: string) => (v ? new Date(`${v}T23:59:59.999`).toISOString() : undefined);

/**
 * Notification delivery history.
 *
 * Read-only, and deliberately so: rendered payloads are erased once a row is
 * terminal, and there is no resend endpoint — anything that sends a new message
 * needs a permission these screens do not carry. This is for answering "did it
 * go, and if not why", nothing more.
 */
export default function NotificationsPage() {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>(ALL);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  /** The row it was opened from — drawn at once while the fresh copy loads. */
  const [opened, setOpened] = useState<AdminNotificationDelivery | null>(null);

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(q.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [q]);

  const rangeBackwards = from !== '' && to !== '' && from > to;

  const list = useQuery({
    queryKey: ['notifications', { page, search, status, from, to }],
    enabled: !rangeBackwards,
    queryFn: () =>
      notifications.listDeliveries({
        page,
        limit: PAGE_SIZE,
        q: search || undefined,
        status: status === ALL ? undefined : (status as NotificationDeliveryStatus),
        from: dayStart(from),
        to: dayEnd(to),
      }),
    // Pending and retrying rows move on their own as the worker runs.
    refetchInterval: 30_000,
  });

  // The row is a snapshot from the last list poll. A delivery being diagnosed
  // is usually one still moving, so the open one is re-read on its own and
  // followed closely until it lands — attempts, the next try and the error all
  // change under it.
  const single = useQuery({
    queryKey: ['notification', opened?.id],
    queryFn: () => notifications.getDelivery(opened?.id as string),
    enabled: opened !== null,
    placeholderData: opened ?? undefined,
    refetchInterval: (query) =>
      query.state.data && !isTerminal(query.state.data.status) ? 5_000 : false,
  });
  const detail = opened ? (single.data ?? opened) : null;

  const rows = list.data?.data ?? [];
  const pagination = list.data?.pagination;
  const unresolved = rows.filter((r) => !isTerminal(r.status)).length;
  const failed = rows.filter((r) => r.status === 'failed').length;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          label="On this page"
          value={String(rows.length)}
          note={pagination ? `of ${pagination.total} matching` : 'loading'}
          density="compact"
        />
        <StatCard
          label="Still in flight"
          value={String(unresolved)}
          note="Pending, processing or retrying"
          density="compact"
        />
        <StatCard
          label="Failed"
          value={String(failed)}
          note={failed === 0 ? 'Nothing to chase' : 'Terminal (will not retry)'}
          density="compact"
        />
      </div>

      <Card
        title={pagination ? `${pagination.total} notifications` : 'Delivery history'}
        action={
          <Toolbar>
            <input
              placeholder="Search recipient, type or message id"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search notifications"
              maxLength={100}
              className={controlCls + ' w-[280px]'}
            />
            <Select
              value={status}
              onChange={(next) => {
                setStatus(next);
                setPage(1);
              }}
              options={[ALL, ...STATUSES].map((s) => ({
                value: s,
                label: s === ALL ? s : statusLabel(s as NotificationDeliveryStatus),
              }))}
              aria-label="Filter by status"
            />
            <input
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(1);
              }}
              aria-label="From date"
              aria-invalid={rangeBackwards || undefined}
              className={controlCls + ' w-[160px]'}
            />
            <input
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(1);
              }}
              aria-label="To date"
              aria-invalid={rangeBackwards || undefined}
              className={controlCls + ' w-[160px]'}
            />
          </Toolbar>
        }
      >
        {rangeBackwards ? (
          <EmptyState
            title="That range runs backwards"
            hint="The From date is after the To date."
          />
        ) : list.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading delivery history…</p>
        ) : list.isError ? (
          <EmptyState
            title="Could not load delivery history"
            hint={
              list.error instanceof ApiError
                ? list.error.message
                : 'The server could not be reached.'
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title="Nothing sent"
            hint="No notification matches these filters. Try widening the dates."
          />
        ) : (
          <>
            <Table head={['Created', 'Recipient', 'Type', 'Status', 'Attempts', 'Delivered']}>
              {rows.map((row) => (
                <Row key={row.id} onClick={() => setOpened(row)}>
                  <Cell className="font-mono text-md">{stamp(row.createdAt)}</Cell>
                  <Cell className="break-all">{row.recipient}</Cell>
                  <Cell>{notificationLabel(row.notificationType)}</Cell>
                  <Cell>
                    <Chip className={'border ' + statusTone(row.status)}>
                      {statusLabel(row.status)}
                    </Chip>
                  </Cell>
                  <Cell className="font-mono text-md tabular-nums">
                    {row.attemptCount}/{row.maxAttempts}
                  </Cell>
                  <Cell className="font-mono text-md">{stamp(row.deliveredAt)}</Cell>
                </Row>
              ))}
            </Table>

            {pagination && pagination.totalPages > 1 && (
              <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                <span className="font-mono text-md text-ink-muted">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="quiet"
                    disabled={pagination.page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="quiet"
                    disabled={!pagination.hasMore}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      <Modal
        open={!!detail}
        onClose={() => setOpened(null)}
        title={detail ? notificationLabel(detail.notificationType) : ''}
      >
        {detail && (
          <div className="flex flex-col gap-4">
            {single.isError && (
              <p
                role="alert"
                className="m-0 rounded-xl border border-warn-edge bg-warn-wash px-4 py-3 text-base text-warn-text"
              >
                Showing the copy from the list, because the latest state could not be read.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Chip className={'border ' + statusTone(detail.status)}>
                {statusLabel(detail.status)}
              </Chip>
              <Chip>{channelLabel(detail.channel)}</Chip>
              <Chip>
                {detail.attemptCount} of {detail.maxAttempts} attempts
              </Chip>
            </div>

            {detail.lastError && (
              <div>
                <span className="font-mono text-sm tracking-wide text-ink-muted uppercase">
                  Last error
                </span>
                <p
                  role="alert"
                  className="m-0 mt-1 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text"
                >
                  {detail.lastError}
                </p>
              </div>
            )}

            <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-5 gap-y-3 text-base">
              <dt className="font-mono text-sm text-ink-muted uppercase">Recipient</dt>
              <dd className="m-0 break-all">{detail.recipient}</dd>

              <dt className="font-mono text-sm text-ink-muted uppercase">Created</dt>
              <dd className="m-0 font-mono">{stamp(detail.createdAt)}</dd>

              <dt className="font-mono text-sm text-ink-muted uppercase">Updated</dt>
              <dd className="m-0 font-mono">{stamp(detail.updatedAt)}</dd>

              <dt className="font-mono text-sm text-ink-muted uppercase">Delivered</dt>
              <dd className="m-0 font-mono">{stamp(detail.deliveredAt)}</dd>

              {detail.nextAttemptAt && (
                <>
                  <dt className="font-mono text-sm text-ink-muted uppercase">Next attempt</dt>
                  <dd className="m-0 font-mono">{stamp(detail.nextAttemptAt)}</dd>
                </>
              )}

              {describeDetail(detail.safeMetadata).map((row) => (
                <Fragment key={row.label}>
                  <dt className="font-mono text-sm text-ink-muted uppercase">{row.label}</dt>
                  <dd className="m-0 break-words">{row.value}</dd>
                </Fragment>
              ))}
            </dl>

            <p className="m-0 border-t border-hairline pt-3 text-base text-ink-muted">
              Subject and body are not held here. They are encrypted while delivery is pending and
              erased once the row reaches a final state, so history keeps only what is safe to
              retain. There is no resend.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}
